import React, { useCallback, useMemo } from "react";
import {
  CygnusThemeSettings,
  type CygnusThemeData,
  type NormalizedCygnusTheme,
  type SaveThemeOptions,
  type ThemeColorMode,
  type ThemeDensity,
} from "@cygnusaiinternal/cygnus-common";
import baseThemes from "../themes.json";
import { useTheme } from "../useTheme";
import { getFromLS, setToLS } from "../../utils/storage";
import { makeCygnusApiCall } from "../../api/api";

type ThemeSelectorProps = {
  setter?: (theme: NormalizedCygnusTheme) => void;
};

type ThemeStore = {
  data?: Record<string, CygnusThemeData>;
};

type ThemeMode = {
  name: ThemeColorMode;
  colors: {
    body: string;
    text: string;
    card_body: string;
  };
};

type PersonalizationParam = {
  parameter_name?: string;
  parameter_value_1?: string;
};

type PersonalizationResponse = {
  data?: Array<{
    params?: PersonalizationParam[];
  }>;
};

const themeKey = (theme: NormalizedCygnusTheme): string =>
  (theme.name || theme.id || "theme")
    .toString()
    .trim()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr: string) => chr.toUpperCase())
    .replace(/^[A-Z]/, (chr: string) => chr.toLowerCase());

const toThemeArray = (themeStore: ThemeStore | Record<string, CygnusThemeData>) => {
  const data = "data" in themeStore ? themeStore.data ?? {} : themeStore;
  return Object.values(data).filter(Boolean);
};

const baseThemeArray = (): CygnusThemeData[] => toThemeArray(baseThemes);

const mergeThemes = (
  defaultThemes: CygnusThemeData[],
  savedThemes: CygnusThemeData[],
): CygnusThemeData[] => {
  const merged = new Map<string, CygnusThemeData>();

  [...defaultThemes, ...savedThemes].forEach((theme) => {
    const key = themeKey(theme as NormalizedCygnusTheme);
    merged.set(key, theme);
  });

  return Array.from(merged.values());
};

const toThemeStore = (themes: NormalizedCygnusTheme[]): ThemeStore => ({
  data: themes.reduce<Record<string, NormalizedCygnusTheme>>((acc, theme) => {
    acc[themeKey(theme) || theme.id] = theme;
    return acc;
  }, {}),
});

const safeJsonParse = <T,>(value?: string | null): T | null => {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const getParamValue = (
  response: PersonalizationResponse | undefined,
  parameterName: string,
): string | undefined =>
  response?.data?.[0]?.params?.find(
    (param) => param.parameter_name === parameterName,
  )?.parameter_value_1;

const applyColorMode = (modeName: ThemeColorMode) => {
  const resolvedMode = (
    modeName === "dark" ? baseThemes.mode.dark : baseThemes.mode.light
  ) as ThemeMode;

  setToLS("mode", resolvedMode);
  document.documentElement.style.setProperty(
    "--body_color",
    resolvedMode.colors.body,
  );
  document.documentElement.style.setProperty(
    "--text_color",
    resolvedMode.colors.text,
  );
  document.documentElement.style.setProperty(
    "--card_body_color",
    resolvedMode.colors.card_body,
  );
};

const applyThemeVariables = (theme: NormalizedCygnusTheme) => {
  document.documentElement.style.setProperty(
    "--theme1_primary",
    theme.colors.primary,
  );
  document.documentElement.style.setProperty(
    "--theme1_secondary",
    theme.colors.secondary,
  );
  document.documentElement.style.setProperty(
    "--theme1_tertiary",
    theme.colors.tertiary || theme.colors.primary,
  );
  document.documentElement.style.setProperty(
    "--theme1_quarternary",
    theme.colors.quarternary || theme.colors.secondary,
  );
};

export default function ThemeSelector({ setter }: ThemeSelectorProps) {
  const { setMode } = useTheme();
  const firmId = getFromLS("firmId") || localStorage.getItem("FirmId");

  const getPersonalizationParam = useCallback(
    async (parameterName: string): Promise<string | undefined> => {
      const response = (await makeCygnusApiCall({
        type: "POST",
        url: "/personalization/get_params/",
        payload: {
          firm_id: firmId,
          parameter_name: parameterName,
        },
        show_message: false,
      })) as PersonalizationResponse | undefined;

      return getParamValue(response, parameterName);
    },
    [firmId],
  );

  const api = useMemo(
    () => ({
      getThemes: async (): Promise<CygnusThemeData[]> => {
        const savedThemesValue = await getPersonalizationParam("themes");
        const savedThemes = safeJsonParse<ThemeStore>(savedThemesValue);
        const localThemes = getFromLS("all-themes") as ThemeStore | null;
        const themeStore = savedThemes || localThemes || baseThemes;
        const mergedThemes = mergeThemes(baseThemeArray(), toThemeArray(themeStore));

        setToLS("all-themes", toThemeStore(mergedThemes as NormalizedCygnusTheme[]));
        return mergedThemes;
      },
      getActiveTheme: async (): Promise<CygnusThemeData> => {
        const savedActiveThemeValue = await getPersonalizationParam("activeTheme");
        const savedActiveTheme =
          safeJsonParse<CygnusThemeData>(savedActiveThemeValue);
        const activeTheme =
          savedActiveTheme ||
          (getFromLS("theme") as CygnusThemeData | null) ||
          baseThemes.data.light;

        setToLS("theme", activeTheme);
        applyThemeVariables(activeTheme as NormalizedCygnusTheme);

        return activeTheme;
      },
      getColorMode: async (): Promise<ThemeColorMode> => {
        const savedColorModeValue =
          await getPersonalizationParam("activeColorMode");
        const savedColorMode =
          safeJsonParse<ThemeColorMode>(savedColorModeValue);

        if (savedColorMode) {
          applyColorMode(savedColorMode);
          return savedColorMode;
        }

        const mode = getFromLS("mode") as ThemeMode | null;
        return mode?.name || "light";
      },
      getDensity: async (): Promise<ThemeDensity> => {
        const savedDensityValue =
          await getPersonalizationParam("informationDensity");
        const savedDensity = safeJsonParse<ThemeDensity>(savedDensityValue);

        if (savedDensity) {
          setToLS("information-density", savedDensity);
          return savedDensity;
        }

        return (getFromLS("information-density") as ThemeDensity | null) || "compact";
      },
      saveTheme: async (
        theme: NormalizedCygnusTheme,
        options: SaveThemeOptions,
      ): Promise<void> => {
        const mergedThemes = mergeThemes(baseThemeArray(), options.themes);
        const updatedThemeStore = toThemeStore(
          mergedThemes as NormalizedCygnusTheme[],
        );

        setToLS("all-themes", updatedThemeStore);
        setToLS("theme", theme);
        setToLS("information-density", options.density);
        applyColorMode(options.colorMode);
        applyThemeVariables(theme);
        setMode(theme);

        setter?.(theme);
        window.dispatchEvent(
          new CustomEvent("cygnus-theme-change", { detail: theme }),
        );

        const payloads = [
          {
            parameter_name: "themes",
            parameter_value_1: JSON.stringify(updatedThemeStore),
          },
          {
            parameter_name: "activeTheme",
            parameter_value_1: JSON.stringify(theme),
          },
          {
            parameter_name: "activeColorMode",
            parameter_value_1: JSON.stringify(options.colorMode),
          },
          {
            parameter_name: "informationDensity",
            parameter_value_1: JSON.stringify(options.density),
          },
        ];

        await Promise.all(
          payloads.map((payload) =>
            makeCygnusApiCall({
              type: "POST",
              url: "/personalization/set_params/",
              payload: {
                firm_id: firmId,
                ...payload,
              },
              show_message: false,
              customized_failure_message: "Unable to save theme settings.",
              customized_error_message:
                "Error occurred while saving theme settings.",
            }),
          ),
        );
      },
    }),
    [getPersonalizationParam, setter, setMode],
  );

  return <CygnusThemeSettings api={api} />;
}
